import styled from 'styled-components';
import { ViewIds } from '@ardoq/api-types';
import { IconName, IconSize } from '@ardoq/icons';
import { createDropdownSliderOptionManager } from '@ardoq/settings-bar';
import {
  type ButtonConfig,
  type DropdownConfig,
  type SettingsConfig,
  SettingsType,
} from '@ardoq/view-settings';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import {
  collapseChildrenDropdownTree,
  INFINITE_DEGREES,
  type DependencyMapDegreesState,
  type DependencyMapViewSettings,
} from '@ardoq/dependency-map';
import { onViewSettingsUpdate as defaultOnViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import { t, type I18nLocale } from '@ardoq/i18n';

/**
 * The slice of an environment's settings controls dependencies that the degree
 * and depth controls read. Every environment's shape satisfies it.
 */
export type DependencyMapDegreeControlsDependencies =
  DependencyMapDegreesState & {
    viewSettings: Pick<
      DependencyMapViewSettings,
      'degreesOfParenthood' | 'degreesOfChildhood' | 'treeDepth'
    >;
  };

export const getColorByReferenceConfig = (
  onViewSettingsUpdate: OnViewSettingsUpdate,
  colorByReference: boolean,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'colorByReference',
  label: t(
    i18nLocale,
    'tabview:dependencyMap.colorByReference',
    'Color by reference type'
  ),
  iconName: IconName.PALETTE,
  isActive: colorByReference,
  type: SettingsType.TOGGLE,
  onViewSettingsUpdate,
});

/**
 * element to display a label adjacent to the degrees sliders in the dropdown menu.
 * these elements are styled to have equal width, so they align nicely when repeated vertically.
 */
const DegreesLabelContainer = styled.div`
  width: 112px;
  text-align: right;
`;

const INCLUDE_REFERENCE_PARENTS_BASE_CONFIG = {
  id: 'includeReferenceParents',
  iconName: IconName.INCLUDE_REFERENCE_PARENTS,
  iconSize: IconSize.LARGE,
};

type IncludeReferenceParentsSliderArgs = {
  dependencies: DependencyMapDegreeControlsDependencies;
  showChildDegreesSlider: boolean;
  i18nLocale: I18nLocale;
};

export const includeReferenceParentsSlider = ({
  dependencies: {
    referenceParentMaxDepth,
    referenceChildMaxDepth,
    canSetParentDegrees,
    canSetChildDegrees,
    hasReferenceParents,
    viewSettings: { degreesOfParenthood, degreesOfChildhood },
  },
  showChildDegreesSlider,
  i18nLocale,
}: IncludeReferenceParentsSliderArgs): DropdownConfig => {
  const getSliderOption = createDropdownSliderOptionManager(
    ViewIds.DEPENDENCY_MAP_2,
    new Map(),
    defaultOnViewSettingsUpdate
  );
  const [parentDegreesSlider, childDegreesSlider] = [
    {
      name: 'degreesOfParenthood',
      label: (
        <DegreesLabelContainer>
          {t(
            i18nLocale,
            'tabview:dependencyMap.parentDegrees',
            'Parent degrees:'
          )}
        </DegreesLabelContainer>
      ),
      degrees: degreesOfParenthood,
      maxDegrees: referenceParentMaxDepth,
      canSetDegrees: canSetParentDegrees,
    },
    {
      name: 'degreesOfChildhood',
      label: (
        <DegreesLabelContainer>
          {t(
            i18nLocale,
            'tabview:dependencyMap.childDegrees',
            'Child degrees:'
          )}
        </DegreesLabelContainer>
      ),
      degrees: degreesOfChildhood,
      maxDegrees: referenceChildMaxDepth,
      canSetDegrees: canSetChildDegrees,
    },
  ].map(({ name, label, degrees, maxDegrees, canSetDegrees }) =>
    getSliderOption({
      name,
      label,
      value: Number.isFinite(degrees) ? degrees : maxDegrees,
      min: 0,
      max: maxDegrees,
      step: 1,
      substituteMax: INFINITE_DEGREES,
      substituteMaxLabel: t(
        i18nLocale,
        'tabview:dependencyMap.unlimited',
        'Unlimited'
      ),
      type: DropdownOptionType.SLIDER,
      isDisabled: !canSetDegrees,
    })
  );
  const isDisabled = !canSetParentDegrees && !canSetChildDegrees;
  return {
    ...INCLUDE_REFERENCE_PARENTS_BASE_CONFIG,
    label: t(
      i18nLocale,
      'tabview:dependencyMap.includeReferenceParents',
      'Include reference parents'
    ),
    type: SettingsType.DROPDOWN,
    isKeepOpen: true,
    isDisabled,
    popoverText: isDisabled
      ? t(
          i18nLocale,
          'tabview:dependencyMap.noReferenceParents',
          'Your data does not have reference parents.'
        )
      : undefined,
    isActive: hasReferenceParents,
    options: showChildDegreesSlider
      ? [parentDegreesSlider, childDegreesSlider]
      : [parentDegreesSlider],
  };
};

export const getCollapseChildrenConfig = ({
  dependencies: {
    maxTreeDepth,
    canSetTreeDepth,
    hasCollapsedNodes,
    viewSettings: { treeDepth },
  },
  onViewSettingsUpdate,
  i18nLocale,
}: {
  dependencies: DependencyMapDegreeControlsDependencies;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  i18nLocale: I18nLocale;
}): ButtonConfig | DropdownConfig =>
  collapseChildrenDropdownTree({
    viewId: ViewIds.DEPENDENCY_MAP_2,
    treeDepth,
    maxTreeDepth,
    canSetTreeDepth,
    hasCollapsedNodes,
    onViewSettingsUpdate,
    labels: {
      changeDepth: t(
        i18nLocale,
        'tabview:dependencyMap.changeDepth',
        'Change depth'
      ),
      collapse: t(i18nLocale, 'tabview:dependencyMap.collapse', 'Collapse'),
      expand: t(i18nLocale, 'tabview:dependencyMap.expand', 'Expand'),
      noDepth: t(
        i18nLocale,
        'tabview:dependencyMap.noDepthToCollapseOrExpand',
        'There is no depth to collapse or expand'
      ),
    },
  });
